import React, { useMemo, useState } from "react";

import { Check, ChevronsUpDown, X } from "lucide-react";
import { useController, useFormContext } from "react-hook-form";

import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/src/components/ui/command";
import { FieldError } from "@/src/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { cn } from "@/src/lib/utils";
import { groupComboboxOptionList } from "@/src/utils/comboboxOptions";

import type { JSX } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

export interface ComboboxOption {
  label: string;
  value: string;
  group?: string;
}

interface MultiComboboxBaseProps {
  options: ComboboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  disabled?: boolean;
  error?: string;
  popoverContainer?: HTMLElement | null;
  popoverClassName?: string;
}

export function MultiComboboxBase({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  disabled,
  error,
  popoverContainer,
  popoverClassName,
}: MultiComboboxBaseProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const toggleValue = (optionValue: string): void => {
    if (value.includes(optionValue)) {
      onChange(value.filter((selectedValue) => selectedValue !== optionValue));
      return;
    }
    onChange([...value, optionValue]);
  };

  const selectedOptionList = options.filter((option) =>
    value.includes(option.value),
  );

  const optionGroupList = useMemo(
    () => groupComboboxOptionList(options),
    [options],
  );

  return (
    <React.Fragment>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            aria-invalid={error ? true : undefined}
            className="bg-card h-auto max-h-[250px] min-h-9 w-full items-start justify-between gap-2 overflow-y-auto py-1.5 text-left whitespace-normal"
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            {selectedOptionList.length > 0 ? (
              <div className="flex flex-1 flex-wrap gap-1">
                {selectedOptionList.map((option) => (
                  <Badge
                    key={option.value}
                    className="pr-1"
                    variant="secondary"
                  >
                    {option.label}
                    <span
                      aria-label={option.label}
                      className="hover:bg-foreground/10 rounded-full p-0.5"
                      onPointerDown={(event) => event.stopPropagation()}
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleValue(option.value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          toggleValue(option.value);
                        }
                      }}
                    >
                      <X className="size-3" />
                    </span>
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground flex-1 text-left">
                {placeholder}
              </span>
            )}
            <ChevronsUpDown className="mt-0.5 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          container={popoverContainer}
          className={cn(
            "w-(--radix-popover-trigger-width) p-0",
            popoverClassName,
          )}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyLabel}</CommandEmpty>
              {optionGroupList.map((optionGroup, index) => (
                <React.Fragment key={optionGroup.heading ?? ""}>
                  {index > 0 ? <CommandSeparator /> : null}
                  <CommandGroup heading={optionGroup.heading}>
                    {optionGroup.options.map((option) => (
                      <CommandItem
                        key={option.value}
                        onSelect={(): void => toggleValue(option.value)}
                        value={`${option.label} ${option.value}`}
                      >
                        <Check
                          className={cn(
                            "mr-2 size-4",
                            value.includes(option.value)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </React.Fragment>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldError>{error}</FieldError>
    </React.Fragment>
  );
}

interface MultiComboboxProps<T extends FieldValues> {
  name: FieldPathByValue<T, string[] | undefined>;
  options: ComboboxOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  disabled?: boolean;
  popoverContainer?: HTMLElement | null;
  popoverClassName?: string;
}

/**
 * Searchable multi-select `Combobox` bound to a react-hook-form `string[]`
 * field via `useController`. The validation error is read from the field state.
 */
export function MultiCombobox<T extends FieldValues>({
  name,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  disabled,
  popoverContainer,
  popoverClassName,
}: MultiComboboxProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  return (
    <MultiComboboxBase
      disabled={disabled}
      emptyLabel={emptyLabel}
      error={fieldState.error?.message}
      onChange={field.onChange}
      options={options}
      placeholder={placeholder}
      popoverClassName={popoverClassName}
      popoverContainer={popoverContainer}
      searchPlaceholder={searchPlaceholder}
      value={field.value ?? []}
    />
  );
}
